import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-bold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_8px_18px_-8px_rgba(76,182,72,0.7)] hover:bg-primary/90 hover:shadow-[0_0_0_4px_rgba(76,182,72,0.22),0_8px_18px_-8px_rgba(76,182,72,0.65)]",
        // El principal de las pantallas con diseño (#2b8229): el verde profundo, con
        // el halo de hover del DS y, deshabilitado, gris pleno (`ink-ghost`) SIN
        // opacidad —el diseño lo dibuja plano, no atenuado—. El `default` sigue
        // siendo el verde de marca del catálogo.
        fuerte:
          "bg-primary-fuerte text-primary-foreground hover:bg-primary-fuerte/90 hover:shadow-[0_0_0_4px_rgba(76,182,72,0.22)] disabled:bg-ink-ghost disabled:opacity-100 disabled:shadow-none",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 hover:shadow-[0_0_0_4px_rgba(229,84,75,0.22)] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-[1.5px] bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        // Affordance de "agregar": superficie de card sobre el canvas + borde
        // punteado. El punteado dice "acá todavía no hay nada, ponelo vos".
        dashed:
          "border-[1.5px] border-dashed border-ink-ghost/70 bg-card text-ink-body hover:border-primary hover:text-primary hover:shadow-[0_0_0_4px_rgba(76,182,72,0.15)]",
        secondary:
          "bg-foreground text-background hover:bg-foreground/90 hover:shadow-[0_0_0_4px_rgba(23,24,27,0.18)] dark:hover:shadow-[0_0_0_4px_rgba(246,246,245,0.18)]",
        soft:
          "bg-accent text-accent-foreground hover:bg-accent/70 hover:shadow-[0_0_0_4px_rgba(76,182,72,0.15)]",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-5 has-[>svg]:px-4",
        sm: "h-9 rounded-sm gap-1.5 px-3.5 text-xs has-[>svg]:px-3",
        lg: "h-[3.25rem] rounded-lg px-7 text-base has-[>svg]:px-5",
        icon: "size-11",
        "icon-sm": "size-9",
        "icon-lg": "size-[3.25rem]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
